// Homeowner portal → Referrals tab (Aug 20, 2026)
//
// Greg: "make a 4th tab 'referrals', give a brief description of the program
// and allow the homeowner to add directly."
//
// Previously the referral form only existed at the standalone /referral route,
// which a customer had to be sent a direct link to. Now it lives where they
// already are.
//
// Two halves: submit a neighbor, and see the ones you've already sent us with
// where each stands. The list comes from ho_list_referrals, a deliberately
// narrow projection -- the customer never sees internal notes, the assigned
// RM, or the referred person's contact details back. Since Oct 20, 2026 each
// one also shows its steps (lib/referralProgress.ts).

import { useCallback, useEffect, useState } from "react";
import {
  authTriple, hoCreateReferral, hoListReferrals, type HoReferral,
} from "../../lib/homeownerRpc";
import {
  REFERRAL_REWARD as FEE, REFERRAL_STEPS, referralStatus, showsSteps, stepReached,
} from "../../lib/referralProgress";

/** Five dots: Sent → Reached out → Inspection → Roof built → $250 paid. */
function ReferralSteps({ reached }: { reached: number }) {
  return (
    <ol className="mt-2.5 flex items-start" aria-label="Referral progress">
      {REFERRAL_STEPS.map((label, i) => {
        const done = i <= reached;
        return (
          <li key={label} className="flex-1 min-w-0 flex flex-col items-center relative"
            aria-current={i === reached ? "step" : undefined}>
            {i > 0 && (
              <span aria-hidden className={`absolute top-[7px] right-1/2 w-full h-0.5 ${
                i <= reached ? "bg-bardin-teal" : "bg-slate-200"}`} />
            )}
            <span aria-hidden className={`relative z-10 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
              done ? "bg-bardin-teal text-white" : "bg-white border-2 border-slate-300"}`}>
              {done ? "✓" : ""}
            </span>
            <span className={`mt-1 text-[10px] leading-tight text-center px-0.5 ${
              done ? "text-bardin-ink font-semibold" : "text-slate-400"}`}>
              {label}
              <span className="sr-only">{done ? " (done)" : " (not yet)"}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function ReferralsTab({
  deal,
}: {
  deal: { id: string; homeowner_name?: string | null; property_street?: string | null };
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [justSent, setJustSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [mine, setMine] = useState<HoReferral[]>([]);
  const [loadingMine, setLoadingMine] = useState(true);

  const loadMine = useCallback(async () => {
    try {
      setMine(await hoListReferrals(authTriple(deal)));
    } catch {
      /* list is additive — a failure just hides it, never blocks submitting */
    } finally {
      setLoadingMine(false);
    }
  }, [deal]);

  useEffect(() => { void loadMine(); }, [loadMine]);

  const canSubmit = name.trim().length >= 2 && phone.trim().length >= 7 && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await hoCreateReferral(authTriple(deal), {
        name: name.trim(), phone: phone.trim(), street: street.trim(),
        city: city.trim(), notes: notes.trim(),
      });
      setJustSent(name.trim().split(" ")[0]);
      setName(""); setPhone(""); setStreet(""); setCity(""); setNotes("");
      void loadMine();
    } catch {
      setErr("We couldn't submit that just now. Please try again, or text us at (470) 823-8816.");
    } finally {
      setSubmitting(false);
    }
  };

  const paidCount     = mine.filter((r) => r.payout_status === "paid").length;
  const approvedCount = mine.filter((r) => !r.superseded && r.payout_status === "approved").length;
  const pendingCount  = mine.filter((r) => !r.superseded && r.payout_status === "earned").length;

  return (
    <div className="space-y-4">
      {/* ─── The program, in two lines ─── */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="text-3xl leading-none">🤝</span>
          <div>
            <h2 className="text-lg font-bold text-bardin-ink">Refer a neighbor, get ${FEE}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Know someone with a roof? Send us their name and number. We'll take a look,
              tell them straight whether there's any damage, and take care of them the way
              we did for you — and once their roof is built, we'll send you{" "}
              <span className="font-semibold text-bardin-orange">${FEE}</span>.
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {[
            { n: "1", t: "You send them over" },
            { n: "2", t: "We inspect the roof" },
            { n: "3", t: `Their roof is built — you get $${FEE}` },
          ].map((s) => (
            <div key={s.n} className="bg-bardin-sky rounded-lg p-2.5">
              <div className="w-5 h-5 rounded-full bg-bardin-teal text-white text-[11px] font-bold flex items-center justify-center mx-auto mb-1">
                {s.n}
              </div>
              <p className="text-[11px] leading-tight text-gray-600">{s.t}</p>
            </div>
          ))}
        </div>

        {(paidCount > 0 || approvedCount > 0 || pendingCount > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {paidCount > 0 && (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                ${paidCount * FEE} paid
              </span>
            )}
            {approvedCount > 0 && (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                ${approvedCount * FEE} approved — on its way
              </span>
            )}
            {pendingCount > 0 && (
              <span className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                ${pendingCount * FEE} being approved
              </span>
            )}
          </div>
        )}
      </div>

      {/* ─── Thanks state ─── */}
      {justSent && (
        <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-emerald-200">
          <p className="text-sm font-semibold text-emerald-800">
            🎉 Thank you! We'll reach out to {justSent} soon.
          </p>
          <p className="text-xs text-gray-600 mt-1">
            We'll keep you posted right here — you'll see their progress below.
          </p>
          <button onClick={() => setJustSent(null)} className="mt-2 text-xs text-bardin-teal font-medium underline">
            Refer someone else
          </button>
        </div>
      )}

      {/* ─── The form ─── */}
      {!justSent && (
        <form onSubmit={submit} className="bg-white rounded-xl p-5 shadow-sm space-y-3">
          <h3 className="font-semibold text-bardin-ink text-sm">Who should we reach out to?</h3>

          <div>
            <label className="block text-sm font-medium text-bardin-ink mb-1">Their name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="First and last name"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-bardin-ink mb-1">Their cell *</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} required
              type="tel" inputMode="tel" placeholder="(555) 123-4567"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-bardin-ink mb-1">Their address</label>
            <input value={street} onChange={(e) => setStreet(e.target.value)}
              placeholder="Street address"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
            <input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
            <p className="text-xs text-gray-400 mt-1">
              Helps us get to the right roof — and makes sure you get credit.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-bardin-ink mb-1">
              Anything we should know?
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="e.g. best time to reach them"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm resize-none focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
          </div>

          {err && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</p>}

          <button type="submit" disabled={!canSubmit}
            className="w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50">
            {submitting ? "Sending…" : "Send referral"}
          </button>
          <p className="text-[11px] text-gray-400 text-center">
            We'll only contact them about their roof.
          </p>
        </form>
      )}

      {/* ─── Their referrals so far ─── */}
      {!loadingMine && mine.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-bardin-ink text-sm mb-3">
            Your referrals ({mine.length})
          </h3>
          <div className="space-y-2">
            {mine.map((r, i) => {
              const st = referralStatus(r);
              return (
                <div key={`${r.referred_name}-${r.created_at}-${i}`}
                  className={`rounded-lg border px-3 py-2.5 ${st.cls}`}>
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-bardin-ink">{r.referred_name}</span>
                    <span className="text-[11px] opacity-70">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5">{st.label}</p>
                  {showsSteps(r) && <ReferralSteps reached={stepReached(r)} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-center text-sm text-gray-400 pb-4">
        Prefer to text? Send it to{" "}
        <a href="sms:+14708238816" className="text-bardin-teal font-medium">(470) 823-8816</a>
      </p>
    </div>
  );
}
